/**
 * tRPC Safety Configuration
 * Enforces global safety limits to prevent infinite loops and excessive memory usage
 */

export const TRPC_SAFETY_CONFIG = {
  // Query retry strategy - exponential backoff with max retries
  retry: (failureCount: number) => {
    if (failureCount > 3) return false; // Max 3 retries
    return Math.min(1000 * Math.pow(2, failureCount), 10000); // Exponential backoff, max 10s
  },

  // Default stale times (in milliseconds)
  staleTime: {
    SHORT: 5000, // 5 seconds - for frequently changing data
    MEDIUM: 30000, // 30 seconds - for moderately changing data
    LONG: 60000, // 1 minute - for stable data
    VERY_LONG: 300000, // 5 minutes - for rarely changing data
  },

  // Garbage collection times (when to remove from cache)
  gcTime: {
    SHORT: 10000, // 10 seconds
    MEDIUM: 60000, // 1 minute
    LONG: 300000, // 5 minutes
    VERY_LONG: 600000, // 10 minutes
  },

  // Request throttling
  throttle: {
    MIN_INTERVAL_MS: 100, // Minimum 100ms between identical requests
    MAX_CONCURRENT_QUERIES: 10, // Max 10 concurrent queries per component
    MAX_REQUESTS_PER_SECOND: 20, // Max 20 requests per second globally
  },

  // Memory management
  memory: {
    PRESSURE_THRESHOLD: 0.85, // 85% heap usage triggers throttling
    CRITICAL_THRESHOLD: 0.95, // 95% heap usage triggers emergency cleanup
  },

  // Circuit breaker settings
  circuitBreaker: {
    FAILURE_THRESHOLD: 3, // Failures before opening circuit
    RESET_TIMEOUT_MS: 5000, // 5 seconds before attempting recovery
  },

  // Query deduplication
  deduplication: {
    ENABLED: true,
    WINDOW_MS: 100, // 100ms window to deduplicate identical requests
  },
};

/**
 * Get recommended query options based on data volatility
 */
export function getQueryOptions(volatility: 'high' | 'medium' | 'low' | 'static') {
  const config = TRPC_SAFETY_CONFIG;

  switch (volatility) {
    case 'high':
      return {
        staleTime: config.staleTime.SHORT,
        gcTime: config.gcTime.SHORT,
        refetchInterval: 5000, // Refetch every 5 seconds
        retry: 1,
      };
    case 'medium':
      return {
        staleTime: config.staleTime.MEDIUM,
        gcTime: config.gcTime.MEDIUM,
        refetchInterval: false, // Manual refetch only
        retry: 2,
      };
    case 'low':
      return {
        staleTime: config.staleTime.LONG,
        gcTime: config.gcTime.LONG,
        refetchInterval: false,
        retry: 2,
      };
    case 'static':
      return {
        staleTime: config.staleTime.VERY_LONG,
        gcTime: config.gcTime.VERY_LONG,
        refetchInterval: false,
        retry: 1,
      };
    default:
      return {
        staleTime: config.staleTime.MEDIUM,
        gcTime: config.gcTime.MEDIUM,
        refetchInterval: false,
        retry: 2,
      };
  }
}

/**
 * Request deduplication map to prevent duplicate concurrent requests
 */
class RequestDeduplicator {
  private pendingRequests = new Map<string, Promise<any>>();
  private requestTimestamps = new Map<string, number>();

  async deduplicate<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const now = Date.now();
    const lastRequestTime = this.requestTimestamps.get(key) || 0;
    const timeSinceLastRequest = now - lastRequestTime;

    // If a request is already pending, return the existing promise
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    // If a request was made recently (within dedup window), return cached result
    if (timeSinceLastRequest < TRPC_SAFETY_CONFIG.deduplication.WINDOW_MS) {
      return this.pendingRequests.get(key) || fn();
    }

    // Execute new request
    const promise = fn();
    this.pendingRequests.set(key, promise);
    this.requestTimestamps.set(key, now);

    try {
      const result = await promise;
      return result;
    } finally {
      this.pendingRequests.delete(key);
    }
  }

  clear() {
    this.pendingRequests.clear();
    this.requestTimestamps.clear();
  }
}

export const requestDeduplicator = new RequestDeduplicator();

/**
 * Memory pressure monitor
 */
export class MemoryPressureMonitor {
  private checkInterval: NodeJS.Timeout | null = null;
  private listeners: ((pressure: 'normal' | 'high' | 'critical') => void)[] = [];

  start(intervalMs = 5000) {
    if (this.checkInterval) return;

    this.checkInterval = setInterval(() => {
      const pressure = this.getPressureLevel();
      this.listeners.forEach(listener => listener(pressure));
    }, intervalMs);
  }

  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  getPressureLevel(): 'normal' | 'high' | 'critical' {
    if (typeof performance !== 'undefined' && (performance as any).memory) {
      const memory = (performance as any).memory;
      const ratio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

      if (ratio > TRPC_SAFETY_CONFIG.memory.CRITICAL_THRESHOLD) {
        return 'critical';
      }
      if (ratio > TRPC_SAFETY_CONFIG.memory.PRESSURE_THRESHOLD) {
        return 'high';
      }
    }
    return 'normal';
  }

  onPressureChange(listener: (pressure: 'normal' | 'high' | 'critical') => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
}

export const memoryMonitor = new MemoryPressureMonitor();
